import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Disc3, Headphones, MapPin, Play, Search, Share2, Star, TrendingUp, X, Sparkles } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { usePlayer } from '../context/PlayerContext';
import { useAuth } from '../context/AuthContext';
import { SongSupportButton } from '../components/producer/SupportButton';
import { API_BASE as API } from '../lib/apiBase';


function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return `${n}`;
}

function getArtistGenres(artist) {
  if (!artist?.genre) return [];
  if (Array.isArray(artist.genre)) return artist.genre.filter(Boolean);
  return String(artist.genre).split(',').map((item) => item.trim()).filter(Boolean);
}

function ArtistCard({ artist, lang }) {
  const artistGenres = getArtistGenres(artist);

  return (
    <Link
      to={`/artist/${artist.id}`}
      data-testid={`artist-card-${artist.id}`}
      className="group block rounded-[28px] border border-pulse-border bg-pulse-surface p-4 hover:-translate-y-1 hover:border-white/20 transition-transform duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-[22px] mb-4">
        <img src={artist.photo} alt={artist.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        <div className="absolute left-4 right-4 bottom-4">
          <p className="font-heading font-bold text-xl tracking-tight">{artist.name}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-pulse-muted">
            <span className="inline-flex items-center gap-1"><MapPin size={11} /> {artist.city || 'España'}</span>
            <span className="inline-flex items-center gap-1"><Play size={11} /> {formatNum(artist.plays)}</span>
            <span className="inline-flex items-center gap-1 text-amber-400">&#9835; {formatNum(artist.corcheas_received || 0)}</span>
          </div>
        </div>
        {artist.available && (
          <span className="absolute top-4 left-4 rounded-full bg-green-500/15 border border-green-500/20 px-2.5 py-1 text-[10px] text-green-300">
            {lang === 'es' ? 'Catálogo activo' : 'Active catalog'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {artistGenres.slice(0, 3).map((genre) => (
          <span key={genre} className="rounded-full bg-pulse-elevated px-2.5 py-1 text-[10px] text-pulse-muted border border-pulse-border">
            {genre}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-pulse-muted">{lang === 'es' ? 'Disponibilidad para eventos desde:' : 'Event availability from:'}</p>
          <p className="text-sm font-semibold text-white">{artist.price_per_show}€ / show</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-pulse-border px-4 py-2 text-sm text-white group-hover:border-pulse-primary group-hover:text-pulse-primary transition-colors duration-300">
          {lang === 'es' ? 'Escuchar y ver perfil' : 'Listen & view profile'} <ChevronRight size={14} />
        </span>
      </div>
    </Link>
  );
}

function SongRow({ song, idx, songs, playSong, lang, token }) {
  const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;

  const shareSong = async (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/artist/${song.artist_id || ''}`;
    const text = `${title} - ${song.artist_name} en PulseMusic`;
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); } catch {}
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
    }
  };

  return (
    <div
      data-testid={`song-row-${song.id}`}
      className="group flex items-center gap-3 rounded-2xl border border-transparent bg-pulse-surface px-3 py-3 hover:-translate-y-0.5 hover:border-white/15 transition-transform duration-300"
    >
      <span className="w-5 text-right font-mono text-sm text-pulse-muted">{idx + 1}</span>
      <button className="flex flex-1 items-center gap-4 min-w-0 text-left" onClick={() => playSong(song, songs, idx)}>
        <img src={song.cover} alt={title} className="w-12 h-12 rounded-xl object-cover" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="truncate text-xs text-pulse-muted">{song.artist_name}</p>
        </div>
      </button>
      <div className="hidden sm:flex items-center gap-1 text-xs text-pulse-muted">
        <Headphones size={10} /> {formatNum(song.plays)}
      </div>
      <SongSupportButton
        songId={song.id}
        artistId={song.artist_id}
        initialCount={song.corcheas_received || 0}
        artistName={song.artist_name}
      />
      <button data-testid={`song-share-${song.id}`} onClick={shareSong} className="p-1 text-pulse-muted hover:text-pulse-primary transition-colors duration-200"><Share2 size={14} /></button>
      <button data-testid={`song-play-${song.id}`} onClick={() => playSong(song, songs, idx)} className="shrink-0 rounded-full bg-white/5 p-2 text-pulse-primary group-hover:bg-pulse-primary group-hover:text-black transition-colors duration-300">
        <Play size={14} />
      </button>
    </div>
  );
}

function CollectionCard({ artist }) {
  const artistGenres = getArtistGenres(artist);

  return (
    <Link
      to={`/artist/${artist.id}`}
      data-testid={`collection-artist-${artist.id}`}
      className="group min-w-[220px] rounded-2xl border border-pulse-border bg-pulse-surface p-4 hover:border-white/20 transition-colors duration-300"
    >
      <div className="flex items-center gap-3">
        <img src={artist.photo} alt={artist.name} className="w-14 h-14 rounded-2xl object-cover" />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-semibold truncate">{artist.name}</p>
          <p className="text-xs text-pulse-muted truncate">{artist.city || 'España'} · {artistGenres.slice(0, 2).join(', ')}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between text-xs text-pulse-muted">
        <span>{formatNum(artist.plays)} plays</span>
        <span className="text-white group-hover:text-pulse-primary transition-colors duration-300">Ver artista</span>
      </div>
    </Link>
  );
}

function PersonalizationSignalCard({ genres, moods, lang }) {
  const hasGenres = (genres || []).length > 0;
  const hasMoods = (moods || []).length > 0;
  if (!hasGenres && !hasMoods) return null;

  const summary = lang === 'es'
    ? `Estamos priorizando canciones y artistas cercanos a ${hasGenres ? genres.slice(0, 3).join(', ') : 'tu perfil'}${hasMoods ? `, con una energía más alineada a ${moods.slice(0, 2).join(' y ')}` : ''}.`
    : `We are prioritizing songs and artists close to ${hasGenres ? genres.slice(0, 3).join(', ') : 'your profile'}${hasMoods ? `, with energy aligned to ${moods.slice(0, 2).join(' and ')}` : ''}.`;

  return (
    <div data-testid="personalization-signal-card" className="rounded-[28px] border border-cyan-500/20 bg-gradient-to-r from-cyan-500/12 via-transparent to-red-500/12 p-5 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p data-testid="personalization-signal-kicker" className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Afinado por tu onboarding' : 'Tuned by your onboarding'}</p>
          <h3 data-testid="personalization-signal-title" className="font-heading font-semibold text-xl md:text-2xl">{lang === 'es' ? 'Tu Home ya está tomando decisiones reales' : 'Your home is already making real choices'}</h3>
          <p data-testid="personalization-signal-copy" className="mt-3 text-sm text-white/78 leading-relaxed">{summary}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="personalization-signal-tags">
          {(genres || []).slice(0, 4).map((item) => <span key={`genre-${item}`} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs">🎵 {item}</span>)}
          {(moods || []).slice(0, 3).map((item) => <span key={`mood-${item}`} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs">✨ {item}</span>)}
        </div>
      </div>
    </div>
  );
}

function ForYouSection({ forYou, forYouMeta, followSuggestedArtist, lang, playSong, token, topTracksRef }) {
  if (!forYou.length) return null;

  return (
    <section data-testid="for-you-section" className="px-4 md:px-12 mt-14">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Para ti' : 'For you'}</p>
          <h2 data-testid="for-you-title" className="font-heading font-bold text-2xl">{lang === 'es' ? 'Una entrada personalizada desde el primer minuto' : 'A personalized entry from minute one'}</h2>
        </div>
        <button
          data-testid="for-you-jump-top-tracks"
          onClick={() => topTracksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="rounded-full border border-pulse-border px-4 py-2 text-sm text-pulse-muted hover:text-white hover:border-white/20 transition-colors duration-300"
        >
          {lang === 'es' ? 'Ver top global también' : 'See global top too'}
        </button>
      </div>

      <PersonalizationSignalCard genres={forYouMeta.genres} moods={forYouMeta.moods} lang={lang} />

      <div className="space-y-2 mt-6">
        {forYou.slice(0, 6).map((song, idx) => (
          <SongRow key={song.id} song={song} idx={idx} songs={forYou} playSong={playSong} lang={lang} token={token} />
        ))}
      </div>

      {forYouMeta.suggested_artists?.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-1">{lang === 'es' ? 'Sugerencias para seguir' : 'Suggested to follow'}</p>
              <h3 data-testid="for-you-suggested-title" className="font-heading font-semibold text-xl">{lang === 'es' ? 'Artistas alineados con lo que elegiste' : 'Artists aligned with what you picked'}</h3>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {forYouMeta.suggested_artists.slice(0, 5).map((artist) => {
              const artistGenres = getArtistGenres(artist);
              return (
              <div key={artist.id} data-testid={`suggested-artist-${artist.id}`} className="rounded-[24px] border border-pulse-border bg-pulse-surface p-4 flex items-center gap-3">
                {artist.photo ? <img src={artist.photo} alt={artist.name} className="w-16 h-16 rounded-2xl object-cover" /> : <div className="w-16 h-16 rounded-2xl bg-pulse-elevated" />}
                <div className="min-w-0 flex-1">
                  <Link to={`/artist/${artist.id}`} className="font-heading font-semibold truncate block">{artist.name}</Link>
                  <p className="text-xs text-pulse-muted truncate">{artistGenres.join(' · ') || (lang === 'es' ? 'Artista sugerido' : 'Suggested artist')}</p>
                  <button data-testid={`follow-suggested-${artist.id}`} onClick={() => followSuggestedArtist(artist.id)} className="mt-3 rounded-full bg-pulse-primary px-3 py-2 text-xs font-semibold text-black">
                    {lang === 'es' ? 'Seguir' : 'Follow'}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function HomePage() {
  const { lang, t } = useLang();
  const { playSong } = usePlayer();
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ranking, setRanking] = useState([]);
  const [curated, setCurated] = useState([]);
  const [events, setEvents] = useState([]);
  const [topSongs, setTopSongs] = useState([]);
  const [forYou, setForYou] = useState([]);
  const [forYouMeta, setForYouMeta] = useState({ genres: [], moods: [], suggested_artists: [], intent: 'discover' });
  const [weekly, setWeekly] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [guidedTipStep, setGuidedTipStep] = useState(0);
  const [showGuidedTip, setShowGuidedTip] = useState(false);
  const [activeList, setActiveList] = useState('ranking');
  const searchTimeout = useRef(null);
  const discoveryRef = useRef(null);
  const topTracksRef = useRef(null);

  const onboardingSummary = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('pulse_onboarding_summary') || 'null');
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/featured-artists`).then((r) => r.json()),
      fetch(`${API}/api/events?limit=6`).then((r) => r.json()),
      fetch(`${API}/api/charts/top-songs?limit=10`).then((r) => r.json()),
      fetch(`${API}/api/discover/for-you?limit=10`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}).then((r) => r.json()),
      fetch(`${API}/api/discover/weekly?limit=10`).then((r) => r.json()),
      token ? fetch(`${API}/api/users/favorites`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()) : Promise.resolve({ favorites: [] }),
      token ? fetch(`${API}/api/library/overview`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()) : Promise.resolve({ new_releases: [] }),
    ]).then(([featured, e, top, fy, wk, fav, lib]) => {
      setRanking(featured.ranking || []);
      setCurated(featured.curated || []);
      setEvents(e.events || []);
      setTopSongs(top.songs || []);
      setForYou(fy.songs || []);
      setForYouMeta({ genres: fy.genres || [], moods: fy.moods || [], suggested_artists: fy.suggested_artists || [], intent: fy.intent || 'discover' });
      setWeekly(wk.songs || []);
      setFavorites(fav.favorites || []);
      setNewReleases(lib.new_releases || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (searchParams.get('welcome') || localStorage.getItem('pulse_show_guided_tips') === '1') {
      setShowGuidedTip(true);
      setGuidedTipStep(0);
    }
  }, [searchParams]);

  const dismissGuidedTip = () => {
    if (guidedTipStep < 2) {
      setGuidedTipStep((current) => current + 1);
      return;
    }
    setShowGuidedTip(false);
    localStorage.removeItem('pulse_show_guided_tips');
    if (searchParams.get('welcome')) {
      const next = new URLSearchParams(searchParams);
      next.delete('welcome');
      setSearchParams(next, { replace: true });
    }
  };

  const followSuggestedArtist = async (artistId) => {
    if (!token) return;
    await fetch(`${API}/api/artists/${artistId}/follow`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    setForYouMeta((current) => ({ ...current, suggested_artists: current.suggested_artists.filter((artist) => artist.id !== artistId) }));
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.trim().length < 1) {
      setSearchResults(null);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      const res = await fetch(`${API}/api/search?q=${encodeURIComponent(q)}&limit=6`);
      const data = await res.json();
      setSearchResults(data);
    }, 300);
  };

  const lists = [
    { id: 'ranking', label: lang === 'es' ? 'En tendencia' : 'Trending now', icon: TrendingUp },
    { id: 'curated', label: lang === 'es' ? 'Selección editorial' : 'Editorial picks', icon: Star },
  ];

  const showPersonalizedForYouFirst = token && forYou.length > 0 && ((forYouMeta.genres || []).length > 0 || (forYouMeta.moods || []).length > 0);

  return (
    <div data-testid="home-page" className="min-h-screen pb-32 overflow-x-hidden">
      <section className="relative min-h-[78vh] overflow-hidden border-b border-white/5">
        <img src="https://images.pexels.com/photos/2167382/pexels-photo-2167382.jpeg" alt="PulseMusic live scene" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_22%,rgba(255,59,48,0.2),transparent_30%),radial-gradient(circle_at_78%_26%,rgba(255,215,0,0.12),transparent_24%),linear-gradient(180deg,rgba(6,6,6,0.2),rgba(6,6,6,0.94))]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27 viewBox=%270 0 200 200%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.7%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27200%27 height=%27200%27 filter=%27url(%23n)%27 opacity=%271%27/%3E%3C/svg%3E")' }} />

        <div className="relative z-10 px-4 md:px-12 pt-24 md:pt-28 pb-14 max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <span data-testid="hero-kicker" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/35 px-4 py-2 text-[11px] uppercase tracking-[0.24em] text-pulse-muted backdrop-blur-xl">
              <Disc3 size={12} className="text-pulse-primary" /> {lang === 'es' ? 'Escucha, guarda y descubre' : 'Listen, save and discover'}
            </span>

            <h1 className="mt-6 max-w-3xl font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95]">PulseMusic</h1>

            <p className="mt-5 max-w-2xl text-sm md:text-base text-white/72 leading-relaxed">
              {lang === 'es'
                ? 'Descubre artistas, música y eventos en un solo lugar.'
                : 'Discover artists, music and events in one place.'}
            </p>

            <p className="mt-3 max-w-2xl text-sm text-pulse-muted leading-relaxed">
              {lang === 'es'
                ? 'Escucha catálogos completos, crea tu colección, sigue la escena y conecta con los artistas que más te mueven.'
                : 'Listen to full catalogs, build your collection, follow the scene and connect with the artists that move you most.'}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="hero-listen-button"
                onClick={() => topTracksRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="rounded-full bg-pulse-primary px-7 py-3 text-sm font-bold text-black hover:bg-pulse-primary-hover transition-colors duration-300"
              >
                {lang === 'es' ? 'Escuchar ahora' : 'Listen now'}
              </button>
              <button
                data-testid="hero-discover-artists-button"
                onClick={() => discoveryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="rounded-full border border-white/15 bg-black/30 px-7 py-3 text-sm font-semibold text-white hover:border-white/30 transition-colors duration-300"
              >
                {lang === 'es' ? 'Explorar artistas' : 'Explore artists'}
              </button>
              <Link
                to="/events"
                data-testid="hero-events-link"
                className="rounded-full border border-white/10 px-6 py-3 text-sm text-pulse-muted hover:text-white hover:border-white/20 transition-colors duration-300"
              >
                {lang === 'es' ? 'Ver mapa de eventos' : 'Open events map'}
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* ── Universe Banner ─────────────────────────────────────── */}
      <section data-testid="universe-banner" className="relative border-b border-white/5 bg-[#060606] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27200%27 height=%27200%27 viewBox=%270 0 200 200%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.65%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27200%27 height=%27200%27 filter=%27url(%23n)%27 opacity=%271%27/%3E%3C/svg%3E")' }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-12 py-12 md:py-16">
          <p className="text-center text-[10px] uppercase tracking-[0.3em] text-pulse-muted mb-2">{t('universe.kicker')}</p>
          <h2 data-testid="universe-title" className="text-center font-heading font-bold text-xl md:text-2xl mb-10">
            {t('universe.title_pre')}<span className="text-pulse-primary">{t('universe.title_accent')}</span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
            {/* Discover */}
            <Link to="/search" data-testid="universe-discover" className="group rounded-2xl border border-white/5 bg-white/[0.02] p-4 md:p-5 hover:border-white/15 hover:bg-white/[0.04] transition-all duration-300">
              <div className="w-11 h-11 rounded-full border border-cyan-400/30 bg-cyan-400/10 flex items-center justify-center mb-3 group-hover:border-cyan-400/50 group-hover:bg-cyan-400/15 transition-all">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22d3ee" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><path d="M11 8v6M8 11h6"/></svg>
              </div>
              <h3 className="font-heading font-semibold text-sm text-white mb-1">{t('universe.discover_title')}</h3>
              <p className="text-[11px] text-pulse-muted leading-relaxed">{t('universe.discover_desc')}</p>
            </Link>

            {/* Live */}
            <Link to="/live" data-testid="universe-live" className="group rounded-2xl border border-white/5 bg-white/[0.02] p-4 md:p-5 hover:border-white/15 hover:bg-white/[0.04] transition-all duration-300">
              <div className="w-11 h-11 rounded-full border border-red-400/30 bg-red-400/10 flex items-center justify-center mb-3 group-hover:border-red-400/50 group-hover:bg-red-400/15 transition-all">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2" fill="#f87171"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/></svg>
              </div>
              <h3 className="font-heading font-semibold text-sm text-white mb-1">{t('universe.live_title')}</h3>
              <p className="text-[11px] text-pulse-muted leading-relaxed">{t('universe.live_desc')}</p>
            </Link>

            {/* Events */}
            <Link to="/events" data-testid="universe-events" className="group rounded-2xl border border-white/5 bg-white/[0.02] p-4 md:p-5 hover:border-white/15 hover:bg-white/[0.04] transition-all duration-300">
              <div className="w-11 h-11 rounded-full border border-amber-400/30 bg-amber-400/10 flex items-center justify-center mb-3 group-hover:border-amber-400/50 group-hover:bg-amber-400/15 transition-all">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <h3 className="font-heading font-semibold text-sm text-white mb-1">{t('universe.events_title')}</h3>
              <p className="text-[11px] text-pulse-muted leading-relaxed">{t('universe.events_desc')}</p>
            </Link>

            {/* Producer */}
            <Link to="/producer" data-testid="universe-producer" className="group rounded-2xl border border-pulse-primary/20 bg-pulse-primary/[0.04] p-4 md:p-5 hover:border-pulse-primary/40 hover:bg-pulse-primary/[0.08] transition-all duration-300">
              <div className="w-11 h-11 rounded-full border border-pulse-primary/40 bg-pulse-primary/15 flex items-center justify-center mb-3 group-hover:border-pulse-primary/60 group-hover:bg-pulse-primary/20 transition-all">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00D4FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              </div>
              <h3 className="font-heading font-semibold text-sm text-pulse-primary mb-1">{t('universe.producer_title')}</h3>
              <p className="text-[11px] text-pulse-muted leading-relaxed">{t('universe.producer_desc')}</p>
            </Link>

            {/* Corcheas */}
            <Link to="/wallet" data-testid="universe-corcheas" className="group rounded-2xl border border-white/5 bg-white/[0.02] p-4 md:p-5 hover:border-white/15 hover:bg-white/[0.04] transition-all duration-300 col-span-2 md:col-span-1">
              <div className="w-11 h-11 rounded-full border border-green-400/30 bg-green-400/10 flex items-center justify-center mb-3 group-hover:border-green-400/50 group-hover:bg-green-400/15 transition-all">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a1 1 0 0 1 1 1v10.27A4 4 0 1 1 11 18.13V4a1 1 0 0 1 1-1z"/><path d="M13 4h5v3h-5z" fill="#4ade80" fillOpacity="0.3"/></svg>
              </div>
              <h3 className="font-heading font-semibold text-sm text-white mb-1">{t('universe.corcheas_title')}</h3>
              <p className="text-[11px] text-pulse-muted leading-relaxed">{t('universe.corcheas_desc')}</p>
            </Link>
          </div>
        </div>
      </section>

      {showSearch && (
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-xl flex flex-col" data-testid="search-overlay">
          <div className="max-w-3xl mx-auto w-full px-4 pt-20">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-pulse-muted" />
                <input data-testid="search-input" type="text" value={searchQuery} onChange={(e) => handleSearch(e.target.value)} placeholder={lang === 'es' ? 'Buscar artistas, canciones o eventos' : 'Search artists, songs or events'} className="w-full rounded-2xl border border-pulse-border bg-pulse-surface pl-12 pr-4 py-4 text-lg focus:ring-2 focus:ring-cyan-500 focus:outline-none" autoFocus />
              </div>
              <button data-testid="close-overlay-search" onClick={() => { setShowSearch(false); setSearchResults(null); setSearchQuery(''); }} className="p-3 text-pulse-muted hover:text-white transition-colors duration-200"><X size={24} /></button>
            </div>

            {searchResults && (
              <div className="space-y-6 max-h-[60vh] overflow-y-auto rounded-[28px] border border-pulse-border bg-pulse-surface p-5">
                {searchResults.artists?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">{lang === 'es' ? 'Artistas' : 'Artists'}</h3>
                    {searchResults.artists.map((artist) => (
                      <Link key={artist.id} to={`/artist/${artist.id}`} onClick={() => setShowSearch(false)} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-pulse-elevated transition-colors duration-200 border border-transparent hover:border-white/10">
                        {artist.photo ? (
                          <img src={artist.photo} alt={artist.name} className="w-12 h-12 rounded-full object-cover border border-white/10" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400/30 to-red-500/20 border border-white/10 flex items-center justify-center text-sm font-bold text-white">
                            {(artist.name || '?').slice(0, 1).toUpperCase()}
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-white truncate">{artist.name || (lang === 'es' ? 'Artista' : 'Artist')}</p>
                          <p className="text-xs text-pulse-muted truncate">{getArtistGenres(artist).join(' · ') || artist.city || (lang === 'es' ? 'Perfil artista' : 'Artist profile')}</p>
                          <p className="text-[11px] text-pulse-muted">{formatNum(artist.plays)} plays</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {searchResults.songs?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">{lang === 'es' ? 'Canciones' : 'Songs'}</h3>
                    {searchResults.songs.map((song, idx) => (
                      <button key={song.id} onClick={() => { playSong(song, searchResults.songs, idx); setShowSearch(false); }} className="w-full flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-pulse-elevated transition-colors duration-200 text-left">
                        <img src={song.cover} alt="" className="w-10 h-10 rounded-xl object-cover" />
                        <div>
                          <p className="text-sm">{lang === 'en' ? (song.title_en || song.title_es) : song.title_es}</p>
                          <p className="text-xs text-pulse-muted">{song.artist_name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {searchResults.events?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">{lang === 'es' ? 'Eventos' : 'Events'}</h3>
                    {searchResults.events.map((event) => (
                      <Link key={event.id} to="/events" onClick={() => setShowSearch(false)} className="flex items-center gap-3 rounded-xl px-3 py-3 hover:bg-pulse-elevated transition-colors duration-200">
                        <img src={event.image} alt="" className="w-10 h-10 rounded-xl object-cover" />
                        <div>
                          <p className="text-sm">{lang === 'en' ? (event.title_en || event.title_es) : event.title_es}</p>
                          <p className="text-xs text-pulse-muted">{event.date}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

              </div>
            )}

            {searchResults && Object.values(searchResults).every((items) => !items || items.length === 0) && (
              <div className="rounded-[28px] border border-pulse-border bg-pulse-surface p-8 text-center text-sm text-pulse-muted">
                {lang === 'es' ? 'Todavía no encontramos resultados útiles. Prueba con nombre artístico, canción o evento.' : 'We could not find useful results yet. Try an artist name, song or event.'}
              </div>
            )}
          </div>
        </div>
      )}

      {token && user?.role === 'artist' && searchParams.get('welcome') === 'artist' && (
        <section data-testid="artist-welcome-banner" className="px-4 md:px-12 mt-10">
          <div className="rounded-[30px] border border-[#FF3B30]/25 bg-gradient-to-r from-[#FF3B30]/12 via-black/40 to-amber-500/10 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Siguiente paso' : 'Next step'}</p>
              <h2 className="font-heading font-bold text-xl md:text-2xl">{lang === 'es' ? 'Tu perfil ya tiene dirección. Ahora el lanzamiento.' : 'Your profile has direction. Now the launch.'}</h2>
              <p className="text-sm text-white/75 mt-2 max-w-xl leading-relaxed">
                {lang === 'es'
                  ? 'Abre tu panel de artista para el resumen o ve directo a subir tu primera canción (derechos + audio). Te guiamos en tres pasos.'
                  : 'Open your artist dashboard for the summary or go straight to uploading your first track (rights + audio). We guide you in three steps.'}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <Link
                data-testid="artist-welcome-dashboard-link"
                to="/artist-dashboard"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('welcome');
                  setSearchParams(next, { replace: true });
                }}
                className="rounded-full bg-pulse-primary px-6 py-3 text-sm font-bold text-black text-center hover:bg-pulse-primary-hover transition-colors"
              >
                {lang === 'es' ? 'Panel de artista' : 'Artist dashboard'}
              </Link>
              <Link
                data-testid="artist-welcome-upload-link"
                to="/profile#upload-song"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('welcome');
                  setSearchParams(next, { replace: true });
                }}
                className="rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white text-center hover:bg-white/5 transition-colors"
              >
                {lang === 'es' ? 'Subir primera canción' : 'Upload first track'}
              </Link>
            </div>
          </div>
        </section>
      )}

      {token && searchParams.get('welcome') === 'prepared' && onboardingSummary && (
        <section data-testid="personalized-welcome-banner" className="px-4 md:px-12 mt-10">
          <div className="rounded-[30px] border border-cyan-500/20 bg-gradient-to-r from-cyan-500/12 via-transparent to-red-500/12 p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Hemos preparado tu experiencia' : 'We prepared your experience'}</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">{lang === 'es' ? 'Para ti' : 'For you'}</h2>
            <div className="flex flex-wrap gap-2 mt-4">
              {(onboardingSummary.genres || []).slice(0, 4).map((item) => <span key={item} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs">🎵 {item}</span>)}
              {(onboardingSummary.moods || []).slice(0, 3).map((item) => <span key={item} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs">✨ {item}</span>)}
            </div>
          </div>
        </section>
      )}

      {showGuidedTip && (
        <section className="px-4 md:px-12 mt-6">
          <div className="rounded-[26px] border border-white/10 bg-black/45 backdrop-blur-xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Guía rápida' : 'Quick guide'}</p>
              {guidedTipStep === 0 && <p className="text-sm text-white/80">{lang === 'es' ? 'Tu sección “Para ti” ya está filtrando por tus géneros y moods para que la entrada se sienta personalizada de verdad.' : 'Your “For you” section is already using your genres and moods so the entry feels truly personal.'}</p>}
              {guidedTipStep === 1 && <p className="text-sm text-white/80">{lang === 'es' ? 'En “En vivo” vas a encontrar experiencias, juegos, votaciones y dinámicas en tiempo real.' : 'Inside “Live” you will find experiences, games, voting and real-time dynamics.'}</p>}
              {guidedTipStep === 2 && <p className="text-sm text-white/80">{lang === 'es' ? 'Guarda y sigue artistas: cuanto más lo hagas, mejor se vuelve tu feed.' : 'Save and follow artists: the more you do it, the smarter your feed gets.'}</p>}
            </div>
            <button data-testid="guided-tip-next" onClick={dismissGuidedTip} className="rounded-full bg-pulse-primary px-5 py-3 text-sm font-bold text-black self-start md:self-auto">
              {guidedTipStep < 2 ? (lang === 'es' ? 'Siguiente' : 'Next') : (lang === 'es' ? 'Empezar a explorar' : 'Start exploring')}
            </button>
          </div>
        </section>
      )}

      {showPersonalizedForYouFirst && (
        <ForYouSection
          forYou={forYou}
          forYouMeta={forYouMeta}
          followSuggestedArtist={followSuggestedArtist}
          lang={lang}
          playSong={playSong}
          token={token}
          topTracksRef={topTracksRef}
        />
      )}

      {token && favorites.length > 0 && (
        <section className="px-4 md:px-12 mt-12">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Tu colección' : 'Your collection'}</p>
              <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Los artistas que ya te estás guardando' : 'Artists you are already saving'}</h2>
            </div>
            <Link to="/profile" data-testid="home-collection-link" className="text-sm text-pulse-primary hover:text-pulse-primary-hover transition-colors duration-300">
              {lang === 'es' ? 'Abrir mi perfil' : 'Open my profile'}
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {favorites.slice(0, 8).map((artist) => <CollectionCard key={artist.id} artist={artist} />)}
          </div>
        </section>
      )}

      <section ref={discoveryRef} className="px-4 md:px-12 mt-14">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Conoce artistas' : 'Meet artists'}</p>
            <h2 className="font-heading font-bold text-2xl md:text-3xl">{lang === 'es' ? 'Descubre perfiles con catálogo real y presencia en escena' : 'Discover artists with real catalog and scene presence'}</h2>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-pulse-border bg-pulse-surface p-1">
            {lists.map((item) => (
              <button key={item.id} data-testid={`tab-${item.id}`} onClick={() => setActiveList(item.id)} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors duration-300 ${activeList === item.id ? 'bg-pulse-primary text-black font-bold' : 'text-pulse-muted hover:text-white'}`}>
                <item.icon size={14} /> {item.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((item) => <div key={item} className="h-80 rounded-[28px] shimmer-loading" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeList === 'ranking' ? ranking : curated).map((artist) => <ArtistCard key={artist.id} artist={artist} lang={lang} />)}
          </div>
        )}
      </section>

      <section ref={topTracksRef} className="px-4 md:px-12 mt-14">
        <div className="grid gap-6 xl:grid-cols-[1.25fr,0.95fr]">
          <div className="rounded-[30px] border border-pulse-border bg-pulse-surface p-6 md:p-8">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Escucha ahora' : 'Listen now'}</p>
                <h2 className="font-heading font-bold text-2xl md:text-3xl">{lang === 'es' ? 'Top Tracks para descubrir ahora' : 'Top tracks to discover now'}</h2>
              </div>
              <button data-testid="home-search-open" onClick={() => setShowSearch(true)} className="inline-flex items-center gap-2 rounded-full border border-pulse-border px-4 py-2 text-sm text-pulse-muted hover:text-white hover:border-white/20 transition-colors duration-300">
                <Search size={14} /> {lang === 'es' ? 'Buscar música' : 'Search music'}
              </button>
            </div>
            <div className="space-y-2">
              {topSongs.slice(0, 10).map((song, idx) => (
                <SongRow key={song.id} song={song} idx={idx} songs={topSongs} playSong={playSong} lang={lang} token={token} />
              ))}
            </div>
          </div>

          <div className="rounded-[30px] border border-pulse-border bg-gradient-to-br from-pulse-surface to-pulse-elevated p-6 md:p-8">
            <div className="flex items-center justify-between gap-3 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Novedades para fans' : 'New releases for fans'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Lo último de los artistas que sigues' : 'Latest drops from the artists you follow'}</h2>
              </div>
              <Sparkles size={18} className="text-pulse-primary" />
            </div>
            {token && newReleases.length > 0 ? (
              <div className="space-y-3">
                {newReleases.slice(0, 5).map((song, idx) => {
                  const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                  return (
                    <button key={song.id} data-testid={`home-new-release-${song.id}`} onClick={() => playSong(song, newReleases, idx)} className="w-full rounded-2xl border border-white/8 bg-black/25 px-4 py-3 text-left flex items-center gap-3 hover:border-white/15 transition-colors duration-300">
                      <img src={song.cover} alt={title} className="w-12 h-12 rounded-xl object-cover" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{title}</p>
                        <p className="truncate text-xs text-pulse-muted">{song.artist_name}</p>
                      </div>
                      <Play size={16} className="text-pulse-primary shrink-0" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/8 bg-black/25 px-4 py-4 text-sm text-pulse-muted leading-relaxed">
                {lang === 'es' ? 'Cuando te hagas fan de artistas, aquí verás sus nuevos lanzamientos en cuanto aparezcan.' : 'Once you become a fan of artists, their new releases will appear here as soon as they drop.'}
              </div>
            )}
          </div>
        </div>
      </section>

      {weekly.length > 0 && (
        <section className="px-4 md:px-12 mt-14">
          <div className="rounded-[30px] border border-green-500/20 bg-gradient-to-r from-green-500/10 via-transparent to-red-500/10 p-6 md:p-8">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Descubrimiento semanal' : 'Weekly discovery'}</p>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Una mezcla fresca para seguir ampliando oído' : 'A fresh mix to keep stretching your ears'}</h2>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
              {weekly.slice(0, 8).map((song, idx) => {
                const title = lang === 'en' ? (song.title_en || song.title_es) : song.title_es;
                return (
                  <button key={song.id} data-testid={`weekly-song-${song.id}`} onClick={() => playSong(song, weekly, idx)} className="group w-36 shrink-0 text-left">
                    <div className="relative mb-3 aspect-square overflow-hidden rounded-2xl">
                      <img src={song.cover} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-300 flex items-center justify-center">
                        <Play size={26} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" fill="white" />
                      </div>
                    </div>
                    <p className="truncate text-sm font-medium">{title}</p>
                    <p className="truncate text-xs text-pulse-muted">{song.artist_name}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {!showPersonalizedForYouFirst && (
        <ForYouSection
          forYou={forYou}
          forYouMeta={forYouMeta}
          followSuggestedArtist={followSuggestedArtist}
          lang={lang}
          playSong={playSong}
          token={token}
          topTracksRef={topTracksRef}
        />
      )}

      <section className="px-4 md:px-12 mt-14">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-2">{lang === 'es' ? 'Escena y territorio' : 'Scene and territory'}</p>
            <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Eventos para seguir la música en tu país' : 'Events to follow the music in your country'}</h2>
          </div>
          <Link to="/events" data-testid="events-map-link" className="text-sm text-pulse-primary hover:text-pulse-primary-hover transition-colors duration-300">
            {lang === 'es' ? 'Abrir mapa completo' : 'Open full map'}
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-4" style={{ scrollbarWidth: 'none' }}>
          {events.map((event) => {
            const title = lang === 'en' ? (event.title_en || event.title_es) : event.title_es;
            const dateLabel = new Date(event.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { day: 'numeric', month: 'short' });
            return (
              <Link key={event.id} to="/events" data-testid={`home-event-${event.id}`} className="group w-72 shrink-0 overflow-hidden rounded-[28px] border border-pulse-border bg-pulse-surface hover:border-white/20 transition-colors duration-300">
                <div className="relative h-40 overflow-hidden">
                  <img src={event.image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                  <div className="absolute left-4 right-4 bottom-4">
                    <p className="font-heading font-semibold text-lg leading-tight">{title}</p>
                    <p className="mt-1 text-xs text-pulse-muted flex items-center gap-1"><MapPin size={10} /> {event.location?.name || event.location?.venue}</p>
                  </div>
                  <div className="absolute top-4 right-4 rounded-full border border-white/10 bg-black/45 px-3 py-1 text-[11px] text-white/90">
                    {dateLabel}
                  </div>
                </div>
                <div className="p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-pulse-muted">{lang === 'es' ? 'Escena local' : 'Local scene'}</p>
                    <p className="text-sm text-white truncate">{(event.artist_names || []).slice(0, 2).join(', ') || (lang === 'es' ? 'Artistas por confirmar' : 'Artists TBA')}</p>
                  </div>
                  <span className="text-sm font-semibold text-pulse-primary">{event.price}€</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <footer className="px-4 md:px-12 mt-16 mb-8 border-t border-pulse-border pt-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-md">
            <h3 className="font-heading font-bold text-lg text-pulse-primary">PulseMusic</h3>
            <p className="mt-2 text-xs text-pulse-muted leading-relaxed">
              {lang === 'es'
                ? 'Una plataforma para escuchar música, guardar artistas, seguir eventos y activar contratación solo cuando ya conoces la escena.'
                : 'A platform to listen to music, save artists, track events and unlock booking only when you already know the scene.'}
            </p>
          </div>
          <div className="flex gap-4 text-xs text-pulse-muted">
            <Link to="/legal?page=privacy" className="hover:text-white transition-colors duration-300">{lang === 'es' ? 'Privacidad' : 'Privacy'}</Link>
            <Link to="/legal?page=terms" className="hover:text-white transition-colors duration-300">{lang === 'es' ? 'Términos' : 'Terms'}</Link>
            <Link to="/legal?page=cookies" className="hover:text-white transition-colors duration-300">Cookies</Link>
            <Link to="/support" className="hover:text-white transition-colors duration-300">{lang === 'es' ? 'Soporte' : 'Support'}</Link>
          </div>
          <p className="w-full md:w-auto text-[10px] text-pulse-muted text-left md:text-right">© {new Date().getFullYear()} PulseMusic. {lang === 'es' ? 'Escucha, sigue y mueve la escena.' : 'Listen, follow and move the scene.'}</p>
        </div>
      </footer>
    </div>
  );
}